import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Dumbbell } from 'lucide-react'

export default function TermsOfServicePage() {
  useEffect(() => {
    document.title = 'Términos de Servicio | fitbdy'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      'Términos de servicio de fitbdy. Conocé las condiciones de uso de nuestra aplicación de fitness.'
    )
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="min-h-screen bg-[#0C0C1D] text-white">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0C0C1D]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <Dumbbell className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-base font-bold text-white tracking-tight">fitbdy</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
          Términos de Servicio
        </h1>
        <p className="text-sm text-gray-500 mb-12">
          Última actualización: 11 de abril de 2026
        </p>

        <div className="space-y-10 text-gray-300 leading-relaxed">

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">1. Aceptación de los términos</h2>
            <p>
              Al descargar, instalar o utilizar la aplicación fitbdy ("el Servicio"), aceptás estos
              Términos de Servicio. Si no estás de acuerdo con alguna parte de estos términos, no
              debés utilizar el Servicio.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">2. Descripción del Servicio</h2>
            <p>
              fitbdy es una aplicación de fitness que permite a los usuarios crear, gestionar y
              seguir rutinas de entrenamiento. El Servicio incluye:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Creación y gestión de rutinas de entrenamiento</li>
              <li>Registro de entrenamientos, series, pesos y repeticiones</li>
              <li>Generación de rutinas mediante inteligencia artificial</li>
              <li>Funcionalidades sociales (seguir usuarios, compartir rutinas)</li>
              <li>Estadísticas y seguimiento de progreso</li>
              <li>Catálogo de ejercicios y máquinas</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">3. Registro y cuenta</h2>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Debés tener al menos 14 años para usar el Servicio</li>
              <li>Sos responsable de mantener la confidencialidad de tu cuenta y contraseña</li>
              <li>Sos responsable de toda actividad que ocurra bajo tu cuenta</li>
              <li>Debés proporcionar información veraz y actualizada al registrarte</li>
              <li>Nos reservamos el derecho de suspender o eliminar cuentas que violen estos términos</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">4. Uso aceptable</h2>
            <p>Al usar el Servicio, te comprometés a no:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Usar el Servicio para fines ilegales o no autorizados</li>
              <li>Interferir con el funcionamiento del Servicio o sus servidores</li>
              <li>Intentar acceder a cuentas de otros usuarios sin autorización</li>
              <li>Publicar contenido ofensivo, difamatorio o inapropiado</li>
              <li>Usar bots, scrapers u otros métodos automatizados para acceder al Servicio</li>
              <li>Revender o redistribuir el Servicio sin autorización</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">5. Contenido del usuario</h2>
            <p>
              Conservás la propiedad de todo el contenido que creás en el Servicio (rutinas,
              entrenamientos, datos de progreso). Al publicar contenido de forma pública, nos otorgás
              una licencia no exclusiva para mostrarlo dentro de la plataforma a otros usuarios.
            </p>
            <p>
              Podés eliminar tu contenido en cualquier momento. Al eliminar tu cuenta, todos tus datos
              serán eliminados permanentemente dentro de los 30 días siguientes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">6. Inteligencia artificial</h2>
            <p>
              El Servicio ofrece funcionalidades de generación de rutinas mediante inteligencia
              artificial. Respecto a estas funcionalidades:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Las rutinas generadas por IA son sugerencias y no reemplazan el consejo de un profesional de la salud o entrenador certificado</li>
              <li>El usuario es responsable de evaluar si los ejercicios sugeridos son apropiados para su condición física</li>
              <li>Las imágenes y documentos enviados para análisis son procesados de forma segura y no se almacenan permanentemente</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">7. Salud y responsabilidad</h2>
            <p>
              fitbdy es una herramienta de organización y seguimiento de entrenamiento. No somos
              profesionales médicos ni entrenadores certificados. El Servicio no proporciona
              asesoramiento médico.
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Consultá a un profesional de la salud antes de comenzar cualquier programa de ejercicio</li>
              <li>El uso del Servicio es bajo tu propia responsabilidad</li>
              <li>No nos hacemos responsables por lesiones derivadas del uso de rutinas creadas o generadas en la plataforma</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">8. Planes y suscripciones</h2>
            <p>
              El Servicio ofrece un plan gratuito con funcionalidades básicas y planes de pago con
              funcionalidades adicionales. Los detalles de cada plan están disponibles en la aplicación.
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-gray-400">
              <li>Los precios pueden variar y serán comunicados antes de cualquier compra</li>
              <li>Las suscripciones se gestionan a través de la tienda de aplicaciones correspondiente (App Store / Google Play)</li>
              <li>Las políticas de reembolso están sujetas a las condiciones de cada tienda</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">9. Propiedad intelectual</h2>
            <p>
              El Servicio, incluyendo su diseño, código, logos, textos e imágenes, es propiedad de
              fitbdy y está protegido por leyes de propiedad intelectual. No se permite la
              reproducción, distribución o modificación del Servicio sin autorización expresa.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">10. Limitación de responsabilidad</h2>
            <p>
              El Servicio se proporciona "tal cual" y "según disponibilidad". No garantizamos que el
              Servicio sea ininterrumpido, seguro o libre de errores. En la máxima medida permitida
              por la ley, no seremos responsables por daños indirectos, incidentales o consecuentes
              derivados del uso del Servicio.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">11. Modificaciones del Servicio</h2>
            <p>
              Nos reservamos el derecho de modificar, suspender o discontinuar cualquier aspecto del
              Servicio en cualquier momento. Te notificaremos sobre cambios significativos a través
              de la aplicación o por email.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">12. Cambios en los términos</h2>
            <p>
              Podemos actualizar estos Términos de Servicio periódicamente. Los cambios entrarán en
              vigor al ser publicados. El uso continuado del Servicio después de los cambios
              constituye tu aceptación de los nuevos términos.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">13. Ley aplicable</h2>
            <p>
              Estos términos se rigen por las leyes de la República Argentina. Cualquier disputa
              será sometida a la jurisdicción de los tribunales competentes de la Ciudad Autónoma
              de Buenos Aires, Argentina.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-2xl font-bold text-white">14. Contacto</h2>
            <p>
              Si tenés preguntas sobre estos términos, podés contactarnos en:
            </p>
            <ul className="list-none pl-0 space-y-1.5 mt-2 text-gray-400">
              <li>
                <span className="text-gray-200 font-medium">Email:</span>{' '}
                <a href="mailto:contacto@fitbdy.com" className="text-indigo-400 hover:text-indigo-300 hover:underline transition-colors">
                  contacto@fitbdy.com
                </a>
              </li>
              <li>
                <span className="text-gray-200 font-medium">Sitio web:</span> fitbdy.com
              </li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 mt-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-gray-600">
          &copy; {new Date().getFullYear()} fitbdy. Todos los derechos reservados.
        </div>
      </footer>
    </div>
  )
}
